import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  User, Phone, Mail, Lock, Eye, EyeOff,
  Building2, MapPin, FileText, ArrowLeft, ArrowRight, CheckCircle2,
  Shield, Users, BarChart3, Cpu, ShoppingCart,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { FormField } from '@/components/ui/form-field';
import { Alert } from '@/components/ui/alert';
import { useAuth } from '@/hooks/useAuth';
import { DIVISIONS_BD } from '@amarfuel/shared-utils';

// ── Validation Schema ──────────────────────
const registerSchema = z.object({
  // Step 1: Personal Info
  name: z.string().min(2, 'Name must be at least 2 characters').max(100),
  phone: z
    .string()
    .min(1, 'Phone number is required')
    .regex(/^(\+880|880|0)?1[3-9]\d{8}$/, 'Enter a valid BD phone number'),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string().min(1, 'Please confirm your password'),

  // Step 2: Station Info
  stationName: z.string().min(2, 'Station name is required').max(200),
  stationAddress: z.string().min(5, 'Address is required').max(500),
  division: z.string().min(1, 'Division is required'),
  district: z.string().min(1, 'District is required'),
  upazila: z.string().optional(),
  licenseNumber: z.string().min(1, 'BPC license number is required'),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

type RegisterFormData = z.infer<typeof registerSchema>;

// ── Step Config ────────────────────────────
const steps = [
  { id: 1, title: 'Owner Info', icon: User },
  { id: 2, title: 'Station Details', icon: Building2 },
  { id: 3, title: 'Review', icon: CheckCircle2 },
];

// ── Access Features ────────────────────────
const accessFeatures = [
  { icon: BarChart3, label: 'Dashboard & Analytics' },
  { icon: ShoppingCart, label: 'Point of Sale (POS)' },
  { icon: Cpu, label: 'IoT Device Config' },
  { icon: Users, label: 'Staff Management' },
];

// ============================================
// REGISTER PAGE
// ============================================
export default function RegisterPage() {
  const { register: registerStation, loading, error, clearError } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    trigger,
    watch,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    mode: 'onChange',
    defaultValues: {
      name: '', phone: '', email: '', password: '', confirmPassword: '',
      stationName: '', stationAddress: '', division: '', district: '', upazila: '', licenseNumber: '',
    },
  });

  const nextStep = async () => {
    let fieldsToValidate: (keyof RegisterFormData)[] = [];
    if (step === 1) fieldsToValidate = ['name', 'phone', 'password', 'confirmPassword'];
    if (step === 2) fieldsToValidate = ['stationName', 'stationAddress', 'division', 'district', 'licenseNumber'];

    const valid = await trigger(fieldsToValidate);
    if (valid) setStep(step + 1);
  };

  const prevStep = () => {
    clearError();
    setStep(step - 1);
  };

  const onSubmit = async (data: RegisterFormData) => {
    clearError();
    try {
      const { confirmPassword, ...payload } = data;
      await registerStation(payload);
      setSuccess(true);
    } catch {
      // Error handled by hook
    }
  };

  // ── Success State ────────────────────────
  if (success) {
    return (
      <div className="text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
          <CheckCircle2 className="h-8 w-8 text-green-600" />
        </div>
        <h2 className="text-xl font-bold text-slate-900">Registration Submitted!</h2>
        <p className="mt-2 text-sm text-slate-500">
          Your station registration is now under review. Our admin team will verify your details
          and activate your account within 24-48 hours.
        </p>

        {/* Role Access Info */}
        <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50 p-4 text-left">
          <p className="mb-2 text-xs font-bold text-blue-700">
            <Shield className="mr-1 inline h-3.5 w-3.5" />
            Once approved, as Station Owner you can:
          </p>
          <ul className="space-y-1 text-xs text-blue-600">
            <li>• Create Manager, Accountant, POS Operator accounts</li>
            <li>• Control each role's access to Dashboard, IoT, Reports</li>
            <li>• Configure devices and manage inventory</li>
          </ul>
        </div>

        <p className="mt-3 text-sm text-slate-500">
          You'll receive an SMS notification once approved.
        </p>
        <Link to="/login">
          <Button className="mt-5 w-full" size="lg">Go to Login</Button>
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-5 text-center">
        <h2 className="text-xl font-bold text-slate-900">Register Station</h2>
        <p className="mt-1 text-sm text-slate-500">
          Apply to register your fuel station on Amar Fuel
        </p>
      </div>

      {/* Step Indicator */}
      <div className="mb-6 flex items-center justify-center">
        {steps.map((s, idx) => (
          <div key={s.id} className="flex items-center">
            <div className="flex flex-col items-center">
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold transition-all duration-300 ${
                  step > s.id
                    ? 'bg-green-500 text-white'
                    : step === s.id
                      ? 'bg-blue-700 text-white shadow-lg shadow-blue-700/30'
                      : 'bg-slate-200 text-slate-500'
                }`}
              >
                {step > s.id ? <CheckCircle2 className="h-5 w-5" /> : <s.icon className="h-4 w-4" />}
              </div>
              <span className={`mt-1.5 text-[10px] font-medium ${step >= s.id ? 'text-blue-700' : 'text-slate-400'}`}>
                {s.title}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div className={`mx-3 mb-4 h-0.5 w-10 rounded-full transition-colors ${step > s.id ? 'bg-green-500' : 'bg-slate-200'}`} />
            )}
          </div>
        ))}
      </div>

      {error && <Alert variant="error" className="mb-4">{error}</Alert>}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">

        {/* ── Step 1: Personal Info ──────────── */}
        {step === 1 && (
          <>
            <FormField label="Full Name" htmlFor="name" required error={errors.name?.message}>
              <div className="relative">
                <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input id="name" placeholder="আপনার নাম" autoComplete="name"
                  error={!!errors.name} className="pl-10" {...register('name')} />
              </div>
            </FormField>

            <FormField label="Phone Number" htmlFor="phone" required error={errors.phone?.message}>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input id="phone" type="tel" placeholder="01XXXXXXXXX" autoComplete="tel"
                  error={!!errors.phone} className="pl-10" {...register('phone')} />
              </div>
            </FormField>

            <FormField label="Email" htmlFor="email" error={errors.email?.message} description="Optional — for recovery">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input id="email" type="email" placeholder="your@email.com" autoComplete="email"
                  error={!!errors.email} className="pl-10" {...register('email')} />
              </div>
            </FormField>

            <FormField label="Password" htmlFor="password" required error={errors.password?.message}
              description="Minimum 8 characters">
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input id="password" type={showPassword ? 'text' : 'password'}
                  placeholder="Create a strong password" autoComplete="new-password"
                  error={!!errors.password} className="pl-10 pr-10" {...register('password')} />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" tabIndex={-1}>
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </FormField>

            <FormField label="Confirm Password" htmlFor="confirmPassword" required error={errors.confirmPassword?.message}>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input id="confirmPassword" type="password" placeholder="Re-enter password"
                  autoComplete="new-password" error={!!errors.confirmPassword}
                  className="pl-10" {...register('confirmPassword')} />
              </div>
            </FormField>

            <Button type="button" className="w-full" size="lg" onClick={nextStep}>
              Continue <ArrowRight className="h-4 w-4" />
            </Button>
          </>
        )}

        {/* ── Step 2: Station Details ───────── */}
        {step === 2 && (
          <>
            <FormField label="Station Name" htmlFor="stationName" required error={errors.stationName?.message}>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input id="stationName" placeholder="e.g. Padma Filling Station"
                  error={!!errors.stationName} className="pl-10" {...register('stationName')} />
              </div>
            </FormField>

            <FormField label="Station Address" htmlFor="stationAddress" required error={errors.stationAddress?.message}>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input id="stationAddress" placeholder="Full address"
                  error={!!errors.stationAddress} className="pl-10" {...register('stationAddress')} />
              </div>
            </FormField>

            <div className="grid grid-cols-2 gap-3">
              <FormField label="Division" htmlFor="division" required error={errors.division?.message}>
                <Select id="division" error={!!errors.division} {...register('division')}>
                  <option value="">Select Division</option>
                  {DIVISIONS_BD.map((div) => (
                    <option key={div} value={div}>{div}</option>
                  ))}
                </Select>
              </FormField>

              <FormField label="District" htmlFor="district" required error={errors.district?.message}>
                <Input id="district" placeholder="e.g. Dhaka"
                  error={!!errors.district} {...register('district')} />
              </FormField>
            </div>

            <FormField label="Upazila / Thana" htmlFor="upazila">
              <Input id="upazila" placeholder="Optional" {...register('upazila')} />
            </FormField>

            <FormField label="BPC License Number" htmlFor="licenseNumber" required error={errors.licenseNumber?.message}>
              <div className="relative">
                <FileText className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input id="licenseNumber" placeholder="License / Registration Number"
                  error={!!errors.licenseNumber} className="pl-10" {...register('licenseNumber')} />
              </div>
            </FormField>

            <div className="flex gap-3">
              <Button type="button" variant="outline" className="flex-1" size="lg" onClick={prevStep}>
                <ArrowLeft className="h-4 w-4" /> Back
              </Button>
              <Button type="button" className="flex-1" size="lg" onClick={nextStep}>
                Review <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </>
        )}

        {/* ── Step 3: Review & Confirm ──────── */}
        {step === 3 && (
          <>
            <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <h3 className="flex items-center gap-2 font-semibold text-slate-800">
                <User className="h-4 w-4 text-blue-600" /> Owner Info
              </h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-slate-500">Name:</span> <span className="font-medium">{watch('name')}</span></div>
                <div><span className="text-slate-500">Phone:</span> <span className="font-medium">{watch('phone')}</span></div>
                {watch('email') && (
                  <div className="col-span-2"><span className="text-slate-500">Email:</span> <span className="font-medium">{watch('email')}</span></div>
                )}
              </div>

              <div className="my-2 h-px bg-slate-200" />

              <h3 className="flex items-center gap-2 font-semibold text-slate-800">
                <Building2 className="h-4 w-4 text-blue-600" /> Station Details
              </h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="col-span-2"><span className="text-slate-500">Station:</span> <span className="font-medium">{watch('stationName')}</span></div>
                <div className="col-span-2"><span className="text-slate-500">Address:</span> <span className="font-medium">{watch('stationAddress')}</span></div>
                <div><span className="text-slate-500">Division:</span> <span className="font-medium">{watch('division')}</span></div>
                <div><span className="text-slate-500">District:</span> <span className="font-medium">{watch('district')}</span></div>
                <div className="col-span-2"><span className="text-slate-500">License:</span> <span className="font-medium">{watch('licenseNumber')}</span></div>
              </div>
            </div>

            {/* Role-Based Access Preview */}
            <div className="rounded-xl border border-blue-100 bg-blue-50 p-3">
              <p className="mb-2 text-xs font-bold text-blue-700">
                <Shield className="mr-1 inline h-3.5 w-3.5" />
                As Station Owner, you'll manage access for:
              </p>
              <div className="grid grid-cols-2 gap-1.5">
                {accessFeatures.map((f) => (
                  <span key={f.label} className="flex items-center gap-1.5 rounded-lg bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-700">
                    <f.icon className="h-3.5 w-3.5 text-blue-600" /> {f.label}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <input type="checkbox" id="terms" className="mt-1 h-4 w-4 rounded border-slate-300" required />
              <label htmlFor="terms" className="text-xs text-amber-800">
                I confirm that all information is accurate. I agree to Amar Fuel's{' '}
                <a href="#" className="font-medium underline">Terms of Service</a> and{' '}
                <a href="#" className="font-medium underline">Privacy Policy</a>.
              </label>
            </div>

            <div className="flex gap-3">
              <Button type="button" variant="outline" className="flex-1" size="lg" onClick={prevStep}>
                <ArrowLeft className="h-4 w-4" /> Back
              </Button>
              <Button type="submit" className="flex-1" size="lg" loading={loading}>
                Submit Registration
              </Button>
            </div>
          </>
        )}
      </form>

      {/* Login Link */}
      <p className="mt-5 text-center text-sm text-slate-500">
        Already have an account?{' '}
        <Link to="/login" className="font-semibold text-blue-700 hover:underline">
          Sign in
        </Link>
      </p>
    </div>
  );
}
